import { memo, useCallback, useEffect, useRef } from 'react';
import { Turnstile, TurnstileInstance } from '@marsidev/react-turnstile';
import { useConfiguration } from '../../hooks/useConfiguration';

/**
 * The default key that is used to access the token from storage.
 * This is used if you render a single TurnstileCaptchaComponent and use the captcha token once.
 * @type {string}
 */
const defaultCaptchaKey = 'enhancedTrackingSearch';

const normalizeSiteKey = (siteKey?: string): string => {
  return (siteKey ?? '').trim().replace(/[.,;:]$/, '');
};

interface Props {
  resetTracker?: unknown;
  isEnabled: boolean;
  captchaKey?: string;
  onSuccessCallback?: (token: string) => void;
}

export const TurnstileCaptchaComponent = memo(({ captchaKey, isEnabled, resetTracker, onSuccessCallback }: Props) => {
  const ref = useRef<TurnstileInstance>(null);
  const config = useConfiguration();
  const siteKey = normalizeSiteKey((window.location.host.includes('passive-') || window.location.host.includes('local') ? "1x00000000000000000000AA" : config?.cloudFlareSiteKey));
  const storageKey = captchaKey ?? defaultCaptchaKey;

  const handleSuccess = useCallback((token: string) => {
    localStorage.setItem(storageKey, token);
    onSuccessCallback?.(token);
  }, [onSuccessCallback, storageKey]);

  const handleReset = useCallback(() => {
    localStorage.removeItem(storageKey);
    ref.current?.reset();
  }, [storageKey]);

  useEffect(() => {
    return () => {
      localStorage.removeItem(storageKey);
      ref.current?.remove();
    };
  }, [storageKey]);

  useEffect(() => {
    if (resetTracker) {
      handleReset();
    }
  }, [handleReset, resetTracker]);

  if (!isEnabled || !siteKey) {
    return null;
  }

  return (
    <Turnstile
      ref={ref}
      id={storageKey}
      options={{ action: storageKey }}
      siteKey={siteKey}
      onError={handleReset}
      onSuccess={handleSuccess}
      onExpire={handleReset}
    />
  );
});